import { Component, OnInit } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { MainCurrencyDto } from '../../_core/_model/currency';
import { CurrencyPaymentGatewayDto, PaymentGatewayConfigDto } from '../../_core/_model/payment-admin';
import { ApiService } from '../../_core/_services/api.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { MiniCountryDto } from '../../_core/_model/countries';

@Component({
  selector: 'app-payment-gateway-mappings',
  standalone: false,
  templateUrl: './payment-gateway-mappings.html',
  styleUrl: './payment-gateway-mappings.css',
})
export class PaymentGatewayMappings implements OnInit {
  loading = false;
  submitting = false;

  readonly workflowOptions = ['Deposit', 'Withdrawal', 'Both'];
  readonly paymentMethodOptions = [
    { value: 'checkout', label: 'Online Payment / Hosted Checkout' },
    { value: 'card', label: 'Card' },
    { value: 'bank-transfer', label: 'Bank Transfer' },
    { value: 'bank-account', label: 'Bank Account / Direct Debit' },
    { value: 'virtual-account', label: 'Reusable Virtual Account' },
    { value: 'interac', label: 'Interac e-Transfer' },
    { value: 'mobile-money', label: 'Mobile Money' },
    { value: 'mpesa', label: 'M-Pesa' },
    { value: 'ach', label: 'ACH' },
    { value: 'ussd', label: 'USSD' },
    { value: 'opay', label: 'OPay' },
    { value: 'nqr', label: 'NQR' },
    { value: 'internet-banking', label: 'Internet Banking' },
    { value: 'wallet', label: 'Wallet' }
  ];
  readonly channelTypeOptions = [
    { value: 'online-payment', label: 'Online payment' },
    { value: 'instructions', label: 'Transfer instructions' },
    { value: 'virtual-account', label: 'Reusable virtual account' }
  ];
  readonly frontendHintOptions = [
    'redirect',
    'hosted-form',
    'inline-sdk',
    'instructions',
    'virtual-account'
  ];

  mappings: CurrencyPaymentGatewayDto[] = [];
  filteredMappings: CurrencyPaymentGatewayDto[] = [];
  configs: PaymentGatewayConfigDto[] = [];
  currencies: MainCurrencyDto[] = [];
  countries: MiniCountryDto[] = [];

  editingId: string | null = null;
  filterCurrencyCode = '';
  filterProviderCode = '';
  filterWorkflow = '';

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private notify: NotificationService
  ) {
    this.form = this.fb.group({
      currencyCode: ['', Validators.required],
      gatewayConfigId: ['', Validators.required],
      workflow: ['Deposit', Validators.required],
      isDefault: [false],
      isActive: [true],
      priority: [1, Validators.required],
      minAmount: [null],
      maxAmount: [null],
      customerType: [''],
      countryCode: [''],
      paymentMethod: ['checkout', Validators.required],
      frontendHint: ['redirect'],
      displayName: [''],
      description: [''],
      channelType: ['online-payment'],
      providerPaymentOption: [''],
      requireBvn: [false],
      metadataJson: ['']
    });
  }

  ngOnInit(): void {
    this.loadMappings();
    this.loadConfigs();
    this.loadMain();
  }

  loadMappings(): void {
    this.loading = true;
    this.api.getCurrencyPaymentGateways().subscribe({
      next: (res) => {
        this.mappings = res.data || [];
        this.applyFilters();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to load mappings',
        'Error'
      ),
      complete: () => this.loading = false
    });
  }

  loadConfigs(): void {
    this.api.getPaymentGatewayConfigs().subscribe({
      next: (res) => this.configs = res.data || []
    });
  }

  loadMain(): void {
    this.api.getMain().subscribe({
      next: (res) => {
        this.currencies = (res.data?.currencies ?? []).filter(c => c.status === 'Active');
        this.countries = (res.data?.countries ?? []).filter(c => c.status === 'Active');
      }
    });
  }

  applyFilters(): void {
    this.filteredMappings = this.mappings.filter(item => {
      const currencyOk = !this.filterCurrencyCode ||
        item.currencyCode.toLowerCase().includes(this.filterCurrencyCode.toLowerCase());

      const providerOk = !this.filterProviderCode ||
        item.providerCode.toLowerCase().includes(this.filterProviderCode.toLowerCase()) ||
        item.providerName.toLowerCase().includes(this.filterProviderCode.toLowerCase());

      const workflowOk = !this.filterWorkflow ||
        item.workflow?.toLowerCase() === this.filterWorkflow.toLowerCase() ||
        item.workflow?.toLowerCase() === 'both';

      return currencyOk && providerOk && workflowOk;
    });
  }

  submit(): void {
    if (this.form.invalid) return;

    if (!this.isMetadataJsonValid()) {
      this.notify.showError('Advanced Metadata JSON is not valid JSON.', 'Validation');
      return;
    }

    this.submitting = true;

    const payload = {
      currencyCode: (this.form.value.currencyCode || '').trim().toUpperCase(),
      gatewayConfigId: this.form.value.gatewayConfigId || '',
      isDefault: !!this.form.value.isDefault,
      isActive: !!this.form.value.isActive,
      priority: Number(this.form.value.priority || 1),
      minAmount: this.form.value.minAmount,
      maxAmount: this.form.value.maxAmount,
      workflow: this.form.value.workflow || 'Deposit',
      customerType: this.normalizeEmpty(this.form.value.customerType),
      countryCode: this.normalizeUpper(this.form.value.countryCode),
      paymentMethod: this.normalizeEmpty(this.form.value.paymentMethod),
      frontendHint: this.normalizeEmpty(this.form.value.frontendHint),
      metadataJson: this.buildMetadataJson()
    };

    const request$ = this.editingId
      ? this.api.updateCurrencyPaymentGateway(this.editingId, payload)
      : this.api.createCurrencyPaymentGateway(payload);

    request$.subscribe({
      next: () => {
        this.notify.showSuccess(
          this.editingId ? 'Currency mapping updated successfully' : 'Currency mapping created successfully',
          'Success'
        );
        this.resetForm();
        this.loadMappings();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || (this.editingId ? 'Failed to update mapping' : 'Failed to create mapping'),
        'Error'
      ),
      complete: () => this.submitting = false
    });
  }

  edit(item: CurrencyPaymentGatewayDto): void {
    this.editingId = item.id;
    const metadata = this.parseMetadata(item.metadataJson);
    const paymentMethod = item.paymentMethod || 'checkout';

    this.form.patchValue({
      currencyCode: item.currencyCode,
      gatewayConfigId: item.gatewayConfigId,
      workflow: item.workflow || 'Deposit',
      isDefault: item.isDefault,
      isActive: item.isActive,
      priority: item.priority,
      minAmount: item.minAmount ?? null,
      maxAmount: item.maxAmount ?? null,
      customerType: item.customerType ?? '',
      countryCode: item.countryCode ?? '',
      paymentMethod,
      frontendHint: item.frontendHint ?? this.defaultFrontendHint(paymentMethod),
      displayName: metadata['displayName'] || '',
      description: metadata['description'] || '',
      channelType: metadata['channelType'] || this.defaultChannelType(paymentMethod),
      providerPaymentOption: metadata['providerPaymentOption'] || '',
      requireBvn: metadata['requireBvn'] === true,
      metadataJson: item.metadataJson ?? ''
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cancelEdit(): void {
    this.resetForm();
  }

  resetForm(): void {
    this.editingId = null;
    this.form.reset({
      currencyCode: '',
      gatewayConfigId: '',
      workflow: 'Deposit',
      isDefault: false,
      isActive: true,
      priority: 1,
      minAmount: null,
      maxAmount: null,
      customerType: '',
      countryCode: '',
      paymentMethod: 'checkout',
      frontendHint: 'redirect',
      displayName: '',
      description: '',
      channelType: 'online-payment',
      providerPaymentOption: '',
      requireBvn: false,
      metadataJson: ''
    });
  }

  onPaymentMethodChanged(): void {
    const method = this.form.value.paymentMethod || 'checkout';
    this.form.patchValue({
      channelType: this.defaultChannelType(method),
      frontendHint: this.defaultFrontendHint(method)
    });
  }

  makeDefault(item: CurrencyPaymentGatewayDto): void {
    this.api.setCurrencyPaymentGatewayDefault(item.id).subscribe({
      next: () => {
        this.notify.showSuccess('Default mapping updated successfully', 'Success');
        this.loadMappings();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to update default mapping',
        'Error'
      )
    });
  }

  getConfigLabel(id: string): string {
    const config = this.configs.find(x => x.id === id);
    if (!config) return id;
    return `${config.name} - ${config.providerName} - ${config.environment}`;
  }

  toggleStatus(item: CurrencyPaymentGatewayDto): void {
    this.api.toggleCurrencyPaymentGatewayStatus(item.id).subscribe({
      next: () => {
        this.notify.showSuccess('Mapping status updated successfully', 'Success');
        this.loadMappings();
      },
      error: (e) => this.notify.showError(
        e?.error?.Error?.Details || 'Failed to update mapping status',
        'Error'
      )
    });
  }

  private isMetadataJsonValid(): boolean {
    const value = String(this.form.value.metadataJson || '').trim();
    if (!value) return true;

    try {
      const parsed = JSON.parse(value);
      return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed);
    } catch {
      return false;
    }
  }

  private buildMetadataJson(): string | null {
    const raw = this.parseMetadata(this.form.value.metadataJson);
    const displayName = this.normalizeEmpty(this.form.value.displayName);
    const description = this.normalizeEmpty(this.form.value.description);
    const channelType = this.normalizeEmpty(this.form.value.channelType);
    const providerPaymentOption = this.normalizeEmpty(this.form.value.providerPaymentOption);
    const method = (this.form.value.paymentMethod || '').toLowerCase();

    this.setOrDelete(raw, 'displayName', displayName);
    this.setOrDelete(raw, 'description', description);
    this.setOrDelete(raw, 'channelType', channelType);
    this.setOrDelete(raw, 'providerPaymentOption', providerPaymentOption);

    if (method === 'virtual-account' || channelType === 'virtual-account') {
      raw['isVirtualAccount'] = true;
    } else {
      delete raw['isVirtualAccount'];
    }

    if (this.form.value.requireBvn) {
      raw['requireBvn'] = true;
    } else {
      delete raw['requireBvn'];
    }

    return Object.keys(raw).length ? JSON.stringify(raw) : null;
  }

  private parseMetadata(value: string | null | undefined): Record<string, any> {
    if (!value?.trim()) return {};

    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private setOrDelete(target: Record<string, any>, key: string, value: string | null): void {
    if (value) {
      target[key] = value;
    } else {
      delete target[key];
    }
  }

  private defaultChannelType(paymentMethod: string): string {
    const normalized = (paymentMethod || '').toLowerCase();
    if (normalized === 'virtual-account') return 'virtual-account';
    if (normalized === 'interac') return 'instructions';
    return 'online-payment';
  }

  private defaultFrontendHint(paymentMethod: string): string {
    const normalized = (paymentMethod || '').toLowerCase();
    if (normalized === 'virtual-account') return 'virtual-account';
    if (normalized === 'interac') return 'instructions';
    return 'redirect';
  }

  private normalizeEmpty(value: string | null | undefined): string | null {
    const v = (value || '').trim();
    return v ? v : null;
  }

  private normalizeUpper(value: string | null | undefined): string | null {
    const v = (value || '').trim().toUpperCase();
    return v ? v : null;
  }
}
